import { useAuth } from "@contexts/auth";
import { PartnerPageEditor } from "./PartnerPageEditor";
import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Checkbox,
  ColorInput,
  Grid,
  Group,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  SimpleGrid,
  ActionIcon,
  Card,
  ActionIconGroup,
  Switch,
  Paper,
  Divider,
  Title,
  ThemeIcon,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDropzone } from "react-dropzone";
import { 
  IconPhoto, IconPlus, IconTrash, IconWorld, IconSettings, IconAppWindow,
  IconCreditCard,
  IconArrowUp, IconArrowDown, IconEdit, IconStethoscope, IconMicroscope,
  IconFirstAidKit, IconPrescription, IconHome, IconAmbulance
} from "@tabler/icons-react";

import { useUpdatePartner } from "@hooks/mutations/partner/useUpdatePartner";
import { useUpdatePartnerProfile } from "@hooks/mutations/partner/useUpdatePartnerProfile";
import { useUpdatePartnerServiceCategories } from "@hooks/mutations/partner/useUpdatePartnerServiceCategories";
import { Partner, PracticeType, ServiceCategory, ServiceCategoryCard } from "@customTypes/partner";
import { imageFileTypes } from "@constants/fileTypes";
import { generateMantineColors } from "@utils/colors";
import { CustomPhoneInput } from "@components/CustomPhoneInput";

import { StyledEditPartnerModalContent } from "./EditPartnerModal.styled";

const getIconForCategory = (category: string) => {
  switch (category) {
    case 'doctors': return <IconStethoscope size={24} />;
    case 'lab_services': return <IconMicroscope size={24} />;
    case 'consultation_services': return <IconFirstAidKit size={24} />;
    case 'radiology': return <IconPhoto size={24} />;
    case 'pharmacy': return <IconPrescription size={24} />;
    case 'homecare': return <IconHome size={24} />;
    case 'emergency': return <IconAmbulance size={24} />;
    default: return <IconAppWindow size={24} />;
  }
};

interface EditPartnerModalProps {
  opened: boolean;
  onClose: () => void;
  partner: Partner | null; // Selected partner data
  onPartnerUpdated?: (partner: Partner) => void;
}

export const EditPartnerModal = ({ opened, onClose, partner, onPartnerUpdated }: EditPartnerModalProps) => {
  const { t } = useTranslation();
  const { can } = useAuth();
  
  const DEFAULT_CATEGORY_TEMPLATES: Record<ServiceCategory, Partial<ServiceCategoryCard>> = {
    doctors: {
      title: t("template-doctors-title", "Our Doctors"),
      description: t("template-doctors-desc", "Meet our team of experienced medical professionals ready to assist you."),
      button_label: t("template-doctors-btn", "View Doctors"),
      icon: "stethoscope",
    },
    lab_services: {
      title: t("template-lab-title", "Laboratory Analysis"),
      description: t("template-lab-desc", "State-of-the-art diagnostic testing and clinical laboratory services."),
      button_label: t("template-lab-btn", "Book Analysis"),
      icon: "microscope",
    },
    consultation_services: {
      title: t("template-consultation-title", "Consultations"),
      description: t("template-consultation-desc", "General and specialized medical consultations for all your needs."),
      button_label: t("template-consultation-btn", "Book Now"),
      icon: "first-aid",
    },
    radiology: {
      title: t("template-radiology-title", "Radiology & Imaging"),
      description: t("template-radiology-desc", "Advanced imaging services including X-Ray, Ultrasound, and more."),
      button_label: t("template-radiology-btn", "View Services"),
      icon: "photo",
    },
    pharmacy: {
      title: t("template-pharmacy-title", "Pharmacy"),
      description: t("template-pharmacy-desc", "Prescription fulfillment and over-the-counter medications."),
      button_label: t("template-pharmacy-btn", "Order Meds"),
      icon: "prescription",
    },
    homecare: {
      title: t("template-homecare-title", "Homecare Services"),
      description: t("template-homecare-desc", "Quality medical care in the comfort of your own home."),
      button_label: t("template-homecare-btn", "Request Homecare"),
      icon: "home",
    },
    emergency: {
      title: t("template-emergency-title", "Emergency Care"),
      description: t("template-emergency-desc", "24/7 urgent medical response and critical care services."),
      button_label: t("template-emergency-btn", "Call Now"),
      icon: "ambulance",
    },
  };

  const { mutate: updatePartner, isLoading: isUpdating } = useUpdatePartner(partner?._id || "");
  const { mutate: updateProfile, isLoading: isUpdatingProfile } = useUpdatePartnerProfile(partner?._id || "");
  const { mutate: updateCategories, isLoading: isUpdatingCategories } = useUpdatePartnerServiceCategories();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategoryCard[]>([]);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);

  const form = useForm({
    initialValues: {
      name: "",
      enterprise_id: "",
      practice_type: "clinic" as PracticeType,
      is_organization: true,
      contact_email: "",
      contact_phone: "",
      address: "",
      city: "",
      country: "",
      description: "",
      registration_number: "",
      tax_id: "",
      is_active: true,
      // Profile fields
      slug: "",
      visibility: "public" as "public" | "private",
      website: "",
      brief_description: "",
      specialties: [] as string[],
      accreditations: [] as string[],
      established_year: undefined as number | undefined,
      operating_hours: "",
      facebook: "",
      instagram: "",
      linkedin: "",
      twitter: "",
      primary_color: "#3b82f6",
      accent_color: "#f59e0b",
      payout_method: "",
      bank_account_number: "",
      bank_name: "",
      mobile_money_number: "",
      mobile_money_provider: "",
      mobile_money_country_code: "",
      joonapay_entity_uuid: "",
      joonapay_country_uuid: "",
      joonapay_method_uuid: "",
    },
  });

  const categoryForm = useForm({
    initialValues: {
      category: "doctors" as ServiceCategory,
      title: "",
      description: "",
      button_label: "",
      is_visible: true,
    },
  });

  // Reset form when partner changes
  useEffect(() => {
    if (partner) {
      form.setValues({
        name: partner.name || "",
        enterprise_id: partner.enterprise_id || "",
        practice_type: partner.practice_type || "clinic",
        is_organization: partner.is_organization ?? true,
        contact_email: partner.contact_email || "",
        contact_phone: partner.contact_phone || "",
        address: partner.address || "",
        city: partner.city || "",
        country: partner.country || "",
        description: partner.description || "",
        registration_number: partner.registration_number || "",
        tax_id: partner.tax_id || "",
        is_active: partner.is_active ?? true,
        slug: partner.slug || "",
        visibility: partner.visibility || "public",
        website: partner.website || "",
        brief_description: partner.brief_description || "",
        specialties: partner.specialties || [],
        accreditations: partner.accreditations || [],
        established_year: partner.established_year,
        operating_hours: partner.operating_hours || "",
        facebook: partner.social_media?.facebook || "",
        instagram: partner.social_media?.instagram || "",
        linkedin: partner.social_media?.linkedin || "",
        twitter: partner.social_media?.twitter || "",
        // When loading, we use shade 6 as the representative color if a palette exists
        primary_color: partner.color_palette?.[6] || partner.color_palette?.[0] || "#3b82f6",
        accent_color: partner.accent_color || "#f59e0b",
        payout_method: partner.payout_method || "",
        bank_account_number: partner.bank_account_number || "",
        bank_name: partner.bank_name || "",
        mobile_money_number: partner.mobile_money_number || "",
        mobile_money_provider: partner.mobile_money_provider || "",
        mobile_money_country_code: partner.mobile_money_country_code || "",
        joonapay_entity_uuid: partner.joonapay_entity_uuid || "",
        joonapay_country_uuid: partner.joonapay_country_uuid || "",
        joonapay_method_uuid: partner.joonapay_method_uuid || "",
      });
      setLogoFile(null);
      setGalleryFiles([]);
      setServiceCategories(partner.service_categories || []);
    }
  }, [partner]);

  const applyServerPartner = (updatedPartner: Partner) => {
    onPartnerUpdated?.(updatedPartner);
  };

  const handleSaveGeneral = (values: typeof form.values) => {
    updatePartner({
      name: values.name,
      enterprise_id: values.enterprise_id,
      practice_type: values.practice_type,
      is_organization: values.is_organization,
      contact_email: values.contact_email,
      contact_phone: values.contact_phone,
      address: values.address,
      city: values.city,
      country: values.country,
      description: values.description,
      registration_number: values.registration_number,
      tax_id: values.tax_id,
      is_active: values.is_active,
    }, { onSuccess: applyServerPartner });
  };

  const handleSavePayouts = (values: typeof form.values) => {
    updatePartner({
      payout_method: values.payout_method,
      bank_account_number: values.bank_account_number,
      bank_name: values.bank_name,
      mobile_money_number: values.mobile_money_number,
      mobile_money_provider: values.mobile_money_provider,
      mobile_money_country_code: values.mobile_money_country_code,
    }, { onSuccess: applyServerPartner });
  };

  const handleSaveProfile = (values: typeof form.values) => {
    updatePartner({
      slug: values.slug,
      visibility: values.visibility,
      website: values.website,
      brief_description: values.brief_description,
      specialties: values.specialties,
      accreditations: values.accreditations,
      established_year: values.established_year,
      operating_hours: values.operating_hours,
      social_media: {
        facebook: values.facebook,
        instagram: values.instagram,
        linkedin: values.linkedin,
        twitter: values.twitter,
      },
    }, { onSuccess: applyServerPartner });
  };

  const handleSaveColors = () => {
    updatePartner({
      color_palette: generateMantineColors(form.values.primary_color),
      accent_color: form.values.accent_color,
    }, { onSuccess: applyServerPartner });
  };

  const handleSaveBranding = () => {
    if (!logoFile && galleryFiles.length === 0) return;

    const formData = new FormData();
    if (logoFile) {
      formData.append("logo", logoFile);
    }
    if (galleryFiles.length > 0) {
      galleryFiles.forEach((file) => {
        formData.append("gallery_images", file);
      });
    }
    
    updateProfile(formData, {
        onSuccess: (updatedPartner) => {
            applyServerPartner(updatedPartner);
            setLogoFile(null);
            setGalleryFiles([]);
        }
    });
  };

  const onLogoDrop = useCallback((acceptedFiles: File[]) => {
    setLogoFile(acceptedFiles[0]);
  }, []);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: onLogoDrop,
    accept: { "image/*": imageFileTypes },
    multiple: false,
  });

  const onGalleryDrop = useCallback((acceptedFiles: File[]) => {
    setGalleryFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps: getGalleryRootProps, getInputProps: getGalleryInputProps } = useDropzone({
    onDrop: onGalleryDrop,
    accept: { "image/*": imageFileTypes },
  });

  // Service Category Logic
  const handleAddCategory = () => {
    const template = DEFAULT_CATEGORY_TEMPLATES[categoryForm.values.category];
    categoryForm.setValues({
      category: categoryForm.values.category,
      title: template.title || "",
      description: template.description || "",
      button_label: template.button_label || "",
      is_visible: true,
    });
    setEditingCategoryIndex(-1); // -1 means new category
  };

  const handleEditCategory = (index: number) => {
    const cat = serviceCategories[index];
    categoryForm.setValues({
      category: cat.category,
      title: cat.title,
      description: cat.description,
      button_label: cat.button_label,
      is_visible: cat.is_visible,
    });
    setEditingCategoryIndex(index);
  };

  const handleSaveCategory = (values: typeof categoryForm.values) => {
    const newCategories = [...serviceCategories];
    const categoryCard: ServiceCategoryCard = {
      ...(editingCategoryIndex != null && editingCategoryIndex >= 0 ? newCategories[editingCategoryIndex] : {}),
      ...values,
      display_order: editingCategoryIndex === -1 ? newCategories.length : (newCategories[editingCategoryIndex!]?.display_order || 0),
      count: editingCategoryIndex === -1 ? 0 : (newCategories[editingCategoryIndex!]?.count || 0),
    };

    if (editingCategoryIndex === -1) {
      newCategories.push(categoryCard);
    } else if (editingCategoryIndex !== null) {
      newCategories[editingCategoryIndex] = categoryCard;
    }

    setServiceCategories(newCategories);
    setEditingCategoryIndex(null);
    categoryForm.reset();
  };

  const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
    const newCategories = [...serviceCategories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newCategories.length) {
      const temp = newCategories[index];
      newCategories[index] = newCategories[targetIndex];
      newCategories[targetIndex] = temp;
      
      // Update display orders
      newCategories.forEach((cat, i) => {
        cat.display_order = i;
      });
      
      setServiceCategories(newCategories);
    }
  };

  const handleRemoveCategory = (index: number) => {
    const newCategories = serviceCategories.filter((_, i) => i !== index);
    newCategories.forEach((cat, i) => {
      cat.display_order = i;
    });
    setServiceCategories(newCategories);
  };

  const handleSaveAllCategories = () => {
    if (partner) {
      updateCategories({
        partnerId: partner._id,
        categories: serviceCategories,
      }, { onSuccess: applyServerPartner });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} fz={20}>{t("edit-partner")}</Text>}
      size="xl"
      centered
    >
      <StyledEditPartnerModalContent>
        <Tabs defaultValue="general">
          <Tabs.List mb="md">
            <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>{t("general")}</Tabs.Tab>
            <Tabs.Tab disabled={!can("access.manage")} value="payouts" leftSection={<IconCreditCard size={16} />}>{t("payout-preferences")}</Tabs.Tab>
            <Tabs.Tab value="branding" leftSection={<IconPhoto size={16} />}>{t("branding")}</Tabs.Tab>
            <Tabs.Tab value="profile" leftSection={<IconWorld size={16} />}>{t("seo-profile")}</Tabs.Tab>
            <Tabs.Tab value="page-content">{t("partner-page-content", "Page content")}</Tabs.Tab>
            <Tabs.Tab value="services" leftSection={<IconAppWindow size={16} />}>{t("services-config")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general">
            <form onSubmit={form.onSubmit(handleSaveGeneral)}>
              <Stack gap="md">
                <TextInput label={t("name")} {...form.getInputProps("name")} withAsterisk />
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput label={t("enterprise-id")} {...form.getInputProps("enterprise_id")} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      label={t("practice-type")}
                      data={[
                        { label: t("individual-practice"), value: "individual_practice" },
                        { label: t("group-practice"), value: "group_practice" },
                        { label: t("hospital"), value: "hospital" },
                        { label: t("clinic"), value: "clinic" },
                        { label: t("laboratory"), value: "laboratory" },
                        { label: t("pharmacy"), value: "pharmacy" },
                        { label: t("other"), value: "other" },
                      ]}
                      {...form.getInputProps("practice_type")}
                    />
                  </Grid.Col>
                </Grid>
                <Checkbox
                  label={t("is-organization")}
                  description={t("is-organization-description")}
                  {...form.getInputProps("is_organization", { type: "checkbox" })}
                />
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput label={t("contact-email")} {...form.getInputProps("contact_email")} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput label={t("contact-phone")} {...form.getInputProps("contact_phone")} />
                  </Grid.Col>
                </Grid>
                <TextInput label={t("address")} {...form.getInputProps("address")} />
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput label={t("city")} {...form.getInputProps("city")} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput label={t("country")} {...form.getInputProps("country")} />
                  </Grid.Col>
                </Grid>
                <Textarea label={t("description")} rows={3} {...form.getInputProps("description")} />
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput label={t("registration-number")} {...form.getInputProps("registration_number")} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput label={t("tax-id")} {...form.getInputProps("tax_id")} />
                  </Grid.Col>
                </Grid>
                <Switch
                  label={t("active")}
                  checked={form.values.is_active}
                  onChange={event => form.setFieldValue("is_active", event.currentTarget.checked)}
                />
                <PermissionGate permission="partners.edit"><Button type="submit" loading={isUpdating} mt="md">{t("save-changes")}</Button></PermissionGate>
              </Stack>
            </form>
          </Tabs.Panel>

          <PermissionGate permission="access.manage">          <Tabs.Panel value="payouts">
            <form onSubmit={form.onSubmit(handleSavePayouts)}>
              <Stack gap="md">
                <Title order={5}>{t("payout-preferences")}</Title>
                <Select
                  label={t("payout-method")}
                  placeholder={t("select")}
                  data={[
                    { label: t("bank-transfer"), value: "bank_transfer" },
                    { label: t("mobile-money"), value: "mobile_money" },
                    { label: t("cash"), value: "cash" },
                    { label: t("check"), value: "check" },
                  ]}
                  {...form.getInputProps("payout_method")}
                />

                {form.values.payout_method === "bank_transfer" && (
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput label={t("bank-name")} {...form.getInputProps("bank_name")} />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput label={t("account-number")} {...form.getInputProps("bank_account_number")} />
                    </Grid.Col>
                  </Grid>
                )}

                {form.values.payout_method === "mobile_money" && (
                  <Grid>
                    <Grid.Col span={6}>
                      <Select
                        label={t("mobile-money-provider")}
                        placeholder={t("select")}
                        data={[
                          { label: "Orange Money", value: "ORANGE" },
                          { label: "MTN MoMo", value: "MTN" },
                          { label: "Moov Money", value: "MOOV" },
                          { label: "Wave", value: "WAVE" },
                        ]}
                        {...form.getInputProps("mobile_money_provider")}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Text fw={500} size="sm" mb={4}>{t("mobile-money-number")}</Text>
                      <CustomPhoneInput
                        phone={form.values.mobile_money_number}
                        setPhone={(value) => form.setFieldValue("mobile_money_number", value)}
                        setCountryCode={(value) => form.setFieldValue("mobile_money_country_code", value)}
                      />
                    </Grid.Col>
                  </Grid>
                )}

                <Divider my="sm" label={t("joonapay-config")} labelPosition="center" />
                <Text size="xs" c="dimmed" ta="center" mb="sm">
                  {t("joonapay-config-description", "These UUIDs are resolved and managed automatically from the verified country and provider.")}
                </Text>

                <TextInput 
                  label={t("joonapay-entity-uuid")} 
                  placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                  readOnly
                  {...form.getInputProps("joonapay_entity_uuid")} 
                />
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput 
                      label={t("joonapay-country-uuid")} 
                      readOnly
                      {...form.getInputProps("joonapay_country_uuid")} 
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput 
                      label={t("joonapay-method-uuid")} 
                      readOnly
                      {...form.getInputProps("joonapay_method_uuid")} 
                    />
                  </Grid.Col>
                </Grid>

                <PermissionGate permission="access.manage"><Button type="submit" loading={isUpdating} mt="md">
                  {t("save-payout-settings", "Save Payout Settings")}
                </Button></PermissionGate>
              </Stack>
            </form>
          </Tabs.Panel></PermissionGate>

          <Tabs.Panel value="branding">
            <Stack gap="xl">
              <div>
                <Text fw={500} size="sm" mb="xs">{t("logo")}</Text>
                <Group align="flex-start">
                  <Avatar size={100} src={logoFile ? URL.createObjectURL(logoFile) : partner?.logo} radius="md" />
                  <div {...getLogoRootProps()} className="dropzone-container">
                    <input {...getLogoInputProps()} />
                    <IconPhoto size={24} color="var(--clr-primary-400)" />
                    <Text size="sm" ta="center" c="dimmed">{t("drop-logo-here", "Drop logo here or click to browse")}</Text>
                  </div>
                </Group>
              </div>

              <div>
                <Text fw={500} size="sm" mb="xs">{t("gallery-images")}</Text>
                <div className="media-grid">
                  {partner?.gallery_images?.map((img, i) => (
                    <Avatar key={i} src={img} size={80} radius="md" />
                  ))}
                  {galleryFiles.map((file, i) => (
                    <Avatar key={i} src={URL.createObjectURL(file)} size={80} radius="md" />
                  ))}
                  <div {...getGalleryRootProps()} className="dropzone-container" style={{ height: '80px' }}>
                    <input {...getGalleryInputProps()} />
                    <IconPlus size={20} color="var(--clr-primary-400)" />
                  </div>
                </div>
              </div>

              <PermissionGate permission="partners.edit"><Button onClick={handleSaveBranding} loading={isUpdatingProfile} disabled={!logoFile && galleryFiles.length === 0}>
                {t("upload-media")}
              </Button></PermissionGate>

              <div>
                <Text fw={500} size="sm" mb="xs">{t("color-palette")}</Text>
                <Text size="xs" c="dimmed" mb="md">{t("color-palette-description")}</Text>

                <div className="brand-preview">
                  <div 
                    className="color-box" 
                    style={{ backgroundColor: form.values.primary_color }} 
                    title={t("primary-color")}
                  />
                  <div 
                    className="color-box" 
                    style={{ backgroundColor: form.values.accent_color }} 
                    title={t("accent-color")}
                  />
                  <div className="preview-text">
                    <Text className="preview-title">{partner?.name}</Text>
                    <Text className="preview-subtitle">{t("brand-visual-preview", "Live branding preview")}</Text>
                  </div>
                </div>

                <Grid>
                  <Grid.Col span={6}>
                    <ColorInput 
                      label={t("primary-color")} 
                      {...form.getInputProps("primary_color")} 
                      disallowInput
                    />
                    <Text size="xs" fw={500} mt="sm" mb={4}>{t("generated-primary-palette", "Generated Primary Palette")}</Text>
                    <Group gap={4} wrap="nowrap" style={{ overflowX: 'auto' }}>
                      {generateMantineColors(form.values.primary_color).map((color, idx) => (
                        <div 
                          key={idx}
                          style={{
                            width: '100%',
                            minWidth: '20px',
                            height: '24px',
                            backgroundColor: color,
                            borderRadius: '4px',
                            flex: 1
                          }}
                          title={`Shade ${idx}: ${color}`}
                        />
                      ))}
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <ColorInput 
                      label={t("accent-color")} 
                      {...form.getInputProps("accent_color")} 
                      disallowInput
                    />
                    <Text size="xs" fw={500} mt="sm" mb={4}>{t("generated-accent-palette", "Generated Accent Palette")}</Text>
                    <Group gap={4} wrap="nowrap" style={{ overflowX: 'auto' }}>
                      {generateMantineColors(form.values.accent_color).map((color, idx) => (
                        <div 
                          key={idx}
                          style={{
                            width: '100%',
                            minWidth: '20px',
                            height: '24px',
                            backgroundColor: color,
                            borderRadius: '4px',
                            flex: 1
                          }}
                          title={`Shade ${idx}: ${color}`}
                        />
                      ))}
                    </Group>
                  </Grid.Col>
                </Grid>
                <PermissionGate permission="partners.edit"><Button mt="md" onClick={handleSaveColors} loading={isUpdating}>
                  {t("save-colors")}
                </Button></PermissionGate>
              </div>
              </Stack>
              </Tabs.Panel>
          <Tabs.Panel value="profile">
            <form onSubmit={form.onSubmit(handleSaveProfile)}>
              <Stack gap="md">
                <TextInput 
                  label={t("slug")} 
                  description={t("slug-description")}
                  placeholder="st-marys-clinic"
                  {...form.getInputProps("slug")} 
                />
                
                <Radio.Group label={t("visibility")} {...form.getInputProps("visibility")}>
                  <Group mt="xs">
                    <Radio value="public" label={t("public")} />
                    <Radio value="private" label={t("private")} />
                  </Group>
                </Radio.Group>

                <TextInput label={t("website")} placeholder="https://..." {...form.getInputProps("website")} />
                
                <Textarea label={t("brief-description")} rows={3} {...form.getInputProps("brief_description")} />

                <TagsInput label={t("specialties")} {...form.getInputProps("specialties")} />
                <TagsInput label={t("accreditations")} {...form.getInputProps("accreditations")} />

                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput label={t("established-year")} hideControls {...form.getInputProps("established_year")} />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput label={t("operating-hours")} placeholder="Mon-Fri: 8am-5pm" {...form.getInputProps("operating_hours")} />
                  </Grid.Col>
                </Grid>

                <Text fw={500} size="sm" mt="md">{t("social-media")}</Text>
                <SimpleGrid cols={2}>
                  <TextInput label={t("facebook")} placeholder="URL" {...form.getInputProps("facebook")} />
                  <TextInput label={t("instagram")} placeholder="URL" {...form.getInputProps("instagram")} />
                  <TextInput label={t("linkedin")} placeholder="URL" {...form.getInputProps("linkedin")} />
                  <TextInput label={t("twitter")} placeholder="URL" {...form.getInputProps("twitter")} />
                </SimpleGrid>

                <PermissionGate permission="partners.edit"><Button type="submit" loading={isUpdating} mt="md">{t("save-profile")}</Button></PermissionGate>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="page-content">
            {partner && <PartnerPageEditor key={partner._id} partner={partner} onSaved={applyServerPartner} />}
          </Tabs.Panel>
          <Tabs.Panel value="services">
             <Stack gap="md" py="xs">
               <Text size="sm" c="dimmed">{t("services-config-description", "Configure which service categories are visible on the partner's public page and how they are displayed.")}</Text>
               
               {editingCategoryIndex === null ? (
                 <>
                   <Group justify="space-between" align="flex-end">
                     <Select
                       label={t("add-category")}
                       placeholder={t("select-category")}
                       data={[
                         { label: t("doctors"), value: "doctors" },
                         { label: t("lab-services"), value: "lab_services" },
                         { label: t("consultation-services"), value: "consultation_services" },
                         { label: t("radiology"), value: "radiology" },
                         { label: t("pharmacy"), value: "pharmacy" },
                         { label: t("homecare"), value: "homecare" },
                         { label: t("emergency"), value: "emergency" },
                       ]}
                       {...categoryForm.getInputProps("category")}
                       style={{ flex: 1 }}
                     />
                     <PermissionGate permission="partners.create"><Button leftSection={<IconPlus size={16} />} onClick={handleAddCategory}>
                       {t("add")}
                     </Button></PermissionGate>
                   </Group>

                   <Divider my="sm" label={t("enabled-categories")} labelPosition="center" />

                   {serviceCategories.length === 0 ? (
                     <Paper withBorder p="xl" bg="gray.0">
                       <Text ta="center" c="dimmed">{t("no-categories-configured", "No service categories configured yet.")}</Text>
                     </Paper>
                   ) : (
                     <Stack gap="sm">
                       {serviceCategories.map((cat, index) => (
                         <Card key={cat.category} withBorder padding="sm" radius="md">
                           <Group justify="space-between">
                             <Group gap="sm">
                               <ThemeIcon variant="light" size="lg" color="blue">
                                 {getIconForCategory(cat.category)}
                               </ThemeIcon>
                               <div>
                                 <Text fw={600} size="sm">{cat.title}</Text>
                                 <Text size="xs" c="dimmed" lineClamp={1}>{cat.description}</Text>
                               </div>
                             </Group>
                             <Group gap={4}>
                               <Switch 
                                 size="xs" 
                                 checked={cat.is_visible} 
                                 onChange={(e) => {
                                   const newCats = [...serviceCategories];
                                   newCats[index].is_visible = e.currentTarget.checked;
                                   setServiceCategories(newCats);
                                 }}
                               />
                               <ActionIconGroup>
                                 <ActionIcon variant="subtle" color="gray" onClick={() => handleMoveCategory(index, 'up')} disabled={index === 0}>
                                   <IconArrowUp size={16} />
                                 </ActionIcon>
                                 <ActionIcon variant="subtle" color="gray" onClick={() => handleMoveCategory(index, 'down')} disabled={index === serviceCategories.length - 1}>
                                   <IconArrowDown size={16} />
                                 </ActionIcon>
                                 <ActionIcon variant="subtle" color="blue" onClick={() => handleEditCategory(index)}>
                                   <IconEdit size={16} />
                                 </ActionIcon>
                                 <PermissionGate permission="partners.delete"><ActionIcon variant="subtle" color="red" onClick={() => handleRemoveCategory(index)}>
                                   <IconTrash size={16} />
                                 </ActionIcon></PermissionGate>
                               </ActionIconGroup>
                             </Group>
                           </Group>
                         </Card>
                       ))}
                       <PermissionGate permission="partners.edit"><Button fullWidth mt="md" onClick={handleSaveAllCategories} loading={isUpdatingCategories}>
                         {t("save-configuration")}
                       </Button></PermissionGate>
                     </Stack>
                   )}
                 </>
               ) : (
                 <Paper withBorder p="md">
                    <Stack gap="md">
                      <Title order={5}>{editingCategoryIndex === -1 ? t("add-new-category") : t("edit-category")}: {categoryForm.values.category}</Title>
                      <TextInput label={t("display-title")} {...categoryForm.getInputProps("title")} withAsterisk />
                      <Textarea label={t("description")} {...categoryForm.getInputProps("description")} rows={3} withAsterisk />
                      <TextInput label={t("button-label")} {...categoryForm.getInputProps("button_label")} withAsterisk />
                      <Switch label={t("visible-on-profile")} {...categoryForm.getInputProps("is_visible", { type: 'checkbox' })} />
                      
                      <Group justify="flex-end" mt="md">
                        <Button variant="subtle" color="gray" onClick={() => setEditingCategoryIndex(null)}>
                          {t("cancel")}
                        </Button>
                        <PermissionGate permission="partners.edit"><Button onClick={() => handleSaveCategory(categoryForm.values)}>
                          {t("confirm")}
                        </Button></PermissionGate>
                      </Group>
                    </Stack>
                 </Paper>
               )}
             </Stack>
          </Tabs.Panel>
        </Tabs>
      </StyledEditPartnerModalContent>
    </Modal>
  );
};
